// src/App.js
import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./App.css";

import Sidebar from "./components/Sidebar/Sidebar";
import Header from "./components/Header/Header";
import Home from "./components/Home/Home";

import Crm from "./components/Crm/Crm";
import CMS from "./components/CMS/CMS";
import PageEditor from "./components/CMS/PageEditor/PageEditor";
import PostEditor from "./components/CMS/PostEditor/PostEditor";
import BulkImport from "./components/BulkImport/BulkImport";
import SmartFlow from "./components/SmartFlow/SmartFlow";
import Integrations from "./components/Integrations/Integrations";
import Whatsapp from "./components/Whatsapp/Whatsapp";
import OperationTeam from "./components/OperationTeam/OperationTeam";
import Relatorios from "./components/Relatorios/Relatorios";
import Settings from "./components/Settings/Settings";
import FullcreenLoader from "./components/FullScreenLoader/FullScreenLoader";
import Login from "./components/Login/Login";
import Register from "./components/Register/Register";
import Logs from "./components/Logs/Logs";
import AIAgent from "./components/AIAgent/AIAgent";
import ActivationModal from "./components/ActivationModal/ActivationModal";
import WorkspaceSelector from "./components/WorkspaceSelector/WorkspaceSelector";

import { useAuthStore } from "./services/useAuthStore";
import useBillingStore from "./services/useBillingStore";
import ProtectedRoute from "./routes/ProtectedRoute";
import ThemeProvider from "./components/ThemeProvider/ThemeProvider";

function AppLayout({ children }) {
  const [showActivationModal, setShowActivationModal] = useState(false);
  const { subscription, fetchSubscription } = useBillingStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Verificar status da subscription ao carregar
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    }
  }, [isAuthenticated, fetchSubscription]);

  // Mostrar modal se precisa de pagamento
  useEffect(() => {
    if (subscription) {
      const needsPayment = subscription.needs_payment ||
        ['pending_payment', 'trialing', 'past_due'].includes(subscription.status);
      setShowActivationModal(needsPayment);
    }
  }, [subscription]);

  return (
    <div className="App">
      <Sidebar />
      <div className="content">
        <Header />
        <main className="main-content">
          <Suspense fallback={<div>Carregando...</div>}>
            {children}
          </Suspense>
        </main>
      </div>

      {/* Modal de ativacao de plano */}
      <ActivationModal
        isOpen={showActivationModal}
        onClose={() => setShowActivationModal(false)}
        canClose={true} // Permite fechar para explorar, mas mostra novamente
      />
    </div>
  );
}



function App() {
  const status = useAuthStore((s) => s.status);
  const initAuthFromMe = useAuthStore((s) => s.initAuthFromMe);


  useEffect(() => {
    if (status === "idle") {
      initAuthFromMe();
    }
  }, [status, initAuthFromMe]);

  if (status === "idle" || status === "loading") {
    return <FullcreenLoader />;
  }

  return (
    <ThemeProvider>
      {/*
        ToastContainer otimizado para React 19:
        - limit={3}: Evita múltiplos toasts simultâneos que causam conflitos DOM
        - containerId: Identificador único para evitar duplicação
        - stacked: Empilha toasts de forma ordenada
        - transition={Slide}: Transição mais suave que reduz conflitos de animação
      */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
        limit={3}
        containerId="main-toast-container"
        stacked
        transition={Slide}
      />
      <Router>
        <Routes>

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/select-workspace"
            element={
              <ProtectedRoute requireWorkspace={false}>
                <WorkspaceSelector />
              </ProtectedRoute>
            }
          />


          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <Home />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <Crm />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cms"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <CMS />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cms/pages/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <PageEditor />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cms/posts/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <PostEditor />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk-import"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <BulkImport />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/smartflow/*"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <SmartFlow />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/integrations"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <Integrations />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/whatsapp"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <Whatsapp />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/operationsteam"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <OperationTeam />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/relatorios"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <Relatorios />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <Logs />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai-agent"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <AIAgent />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/configuracoes"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="w-full h-full">
                    <Settings />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />


        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
